import { useEffect, useState } from "react";

import { loadValidationReport } from "./data";
import type { ValidationReport, ValidationRow } from "./types";

/**
 * Parser QA.
 *
 * Groups are ordered worst pass rate first, so the top of each table is the
 * parser gap that would fix the most plans if addressed next.
 */
export function QaReport() {
  const [report, setReport] = useState<ValidationReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadValidationReport()
      .then(setReport)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) return <p className="error">Could not load the validation report: {error}</p>;
  if (!report) return <p className="hint">Loading validation report...</p>;

  const s = report.summary;

  return (
    <div className="qa">
      <p className="hint">
        Every plan's rate components are run back through our evaluator and compared against both the
        Electricity Facts Label and Power to Choose at 500, 1,000 and 2,000 kWh. A plan passes when it
        agrees within 0.05 cents per kWh, which is half of the tenth-of-a-cent that EFLs round to.
      </p>

      <div className="stats">
        <Stat label="Plans" value={s.total_plans.toLocaleString()} />
        <Stat label="Verified" value={`${(s.pass_rate * 100).toFixed(1)}%`} />
        <Stat label="EFL prices read" value={s.efl_prices_extracted.toLocaleString()} />
        <Stat label="Delivery from EFL" value={s.tdu_from_efl.toLocaleString()} />
        <Stat label="Delivery from tariff table" value={s.tdu_from_table.toLocaleString()} />
        <Stat label="Fitted fallback" value={s.fitted_fallback.toLocaleString()} />
        <Stat label="EFL disagrees with PTC" value={s.efl_vs_ptc_disagreements.toLocaleString()} />
      </div>

      <h3>Tariff table calibration</h3>
      <p className="hint">
        Delivery charges solved back out of cleanly parsed plans, compared against our table. A
        disagreement here usually means the table is stale, which would quietly skew every price in
        that territory.
      </p>
      <table className="grid">
        <thead>
          <tr>
            <th>Utility</th>
            <th>Sample</th>
            <th>Table</th>
            <th>Implied by EFLs</th>
            <th>Difference</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {report.tdu_calibration.map((c) => (
            <tr key={c.tdu}>
              <th scope="row">{c.tdu}</th>
              <td>{c.sample_size}</td>
              <td>
                {c.table.cents_per_kwh.toFixed(4)}&cent; + ${c.table.monthly_usd.toFixed(2)}
              </td>
              <td>
                {c.solved.cents_per_kwh.toFixed(4)}&cent; + ${c.solved.monthly_usd.toFixed(2)}
              </td>
              <td>{c.delta.cents_per_kwh >= 0 ? "+" : ""}{c.delta.cents_per_kwh.toFixed(4)}&cent;</td>
              <td>
                <span className={`tag badge ${c.agrees ? "high" : "low"}`}>
                  {c.agrees ? "agrees" : "check"}
                </span>
              </td>
            </tr>
          ))}
          {report.tdu_calibration.length === 0 && (
            <tr>
              <td colSpan={6}>Not enough cleanly parsed plans to calibrate.</td>
            </tr>
          )}
        </tbody>
      </table>

      <Breakdown title="By EFL host" rows={report.by_efl_host} note="The platform generating the document, which is what determines its layout." />
      <Breakdown title="By retailer" rows={report.by_rep} />
      <Breakdown title="By utility" rows={report.by_tdu} />

      <h3>Worst mismatches</h3>
      <table className="grid">
        <thead>
          <tr>
            <th>Plan</th>
            <th>Computed @1000</th>
            <th>Disclosed @1000</th>
            <th>Worst gap</th>
            <th>Why</th>
          </tr>
        </thead>
        <tbody>
          {report.failures.slice(0, 40).map((f) => (
            <tr key={f.plan_id}>
              <th scope="row">
                <a href={f.efl_url} target="_blank" rel="noreferrer noopener">
                  {f.rep} - {f.product}
                </a>
              </th>
              <td>{f.computed["1000"]?.toFixed(2) ?? "-"}&cent;</td>
              <td>{(f.efl_stated["1000"] ?? f.ptc_stated["1000"])?.toFixed(2) ?? "-"}&cent;</td>
              <td>{(f.delta_vs_ptc ?? f.delta_vs_efl)?.toFixed(2) ?? "-"}&cent;</td>
              <td className="why">{f.issues[0] ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Breakdown({ title, rows, note }: { title: string; rows: ValidationRow[]; note?: string }) {
  const worst = rows.filter((r) => r.pass_rate < 1).slice(0, 15);
  return (
    <>
      <h3>{title}</h3>
      {note && <p className="hint">{note}</p>}
      <table className="grid">
        <thead>
          <tr>
            <th>Name</th>
            <th>Plans</th>
            <th>Verified</th>
            <th>Pass rate</th>
          </tr>
        </thead>
        <tbody>
          {worst.map((r) => (
            <tr key={r.name}>
              <th scope="row">{r.name}</th>
              <td>{r.total}</td>
              <td>{r.high}</td>
              <td>
                <div className="bar">
                  <div className="bar-fill" style={{ width: `${r.pass_rate * 100}%` }} />
                  <span>{(r.pass_rate * 100).toFixed(0)}%</span>
                </div>
              </td>
            </tr>
          ))}
          {worst.length === 0 && (
            <tr>
              <td colSpan={4}>Everything passes.</td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}
